import {
  getValueByClassification,
  getValuesByClassification,
  getClassifiedAs,
  normalizeFieldToArray,
  getAssignedBy,
  getValueOrContent
} from "@thegetty/linkedart.js";
import { getIndexedId } from "@thegetty-private/rad-app-utils";
import { getName } from "@thegetty-private/linked-data.js";
import * as localActorHelpers from "@/helpers/Person";
import { isGroupTheGetty } from "@/helpers/Group";
import { removeEmpty } from "@/helpers/LinkedArt";
import internal from "@/data/internal-ontologies";
import aat from "@/data/aat";
const IDENTIFIED_BY = "identified_by";
const REFERRED_TO_BY = "referred_to_by";
const CLASSIFIED_AS = "classified_as";
const PART_OF = "part_of";
const CHANGED_OWNERSHIP_THROUGH = "changed_ownership_through";

/**
 * Gets the overall provenance remarks from the HumanMadeObject's list of linguistic objects
 *
 * @param {object} humanMadeObject - the JSON-LD object
 *
 * @returns {array} a list of the remarks ordered by the dimension
 */
export function getOverallProvenanceNotes(humanMadeObject) {
  if (humanMadeObject == undefined) {
    return [];
  }
  let referred = normalizeFieldToArray(humanMadeObject, REFERRED_TO_BY);
  let remarks = getClassifiedAs(referred, aat.PROVENANCE_REMARKS);

  if (remarks.length == 0) {
    return [];
  }
  // sort them by the assigned order dimensions
  remarks = sortByAssignedDimension(remarks);
  // just return the values

  return remarks.map((remark) => {
    return getValueOrContent(remark);
  });
}

/**
 * Uses the "assigned" dimension property to sort all of the objects in the array
 *
 * @param {array} array - a list of JSON-LD fragments
 *
 * @returns {array}
 */
export function sortByAssignedDimension(array) {
  return array.sort((a, b) => {
    let aOrder = getAssignedBy(a, "dimension")[0].value;
    let bOrder = getAssignedBy(b, "dimension")[0].value;
    return aOrder > bOrder ? 1 : -1;
  });
}

/**
 * Gets the owner's lugt number
 *
 * @param {object} acquisition - the acquisition object
 *
 * @returns {string|undefined} - the owern's lugt number
 */
export function getLugtNumber(acquisition) {
  let identifiedBy = normalizeFieldToArray(
    getFirstAcquisitionPart(acquisition),
    IDENTIFIED_BY
  );

  let lugtNumbers = getValuesByClassification(identifiedBy, aat.LUGT_NUMBER);
  if (lugtNumbers.length == 0) {
    return undefined;
  }
  return lugtNumbers;
}

/**
 * Traverses the HumanMadeObject to extract out all of the indexedIds for constituents that purchased the object
 *
 * @param {object} humanMadeObject
 *
 * @returns {array} a list of indexedIds
 */
export function getOwnersIndexedIds(humanMadeObject) {
  return getOwners(humanMadeObject, true);
}

/**
 * Gets all of the owner stubs from the HumanMadeObject
 *
 * @param {Object} humanMadeObject  - the JSON-LD HumanMadeObject
 * @param {boolean} returnIdsOnly - whether to return just the list of indexedIds (default false)
 *
 * @returns {array} - the list of stub constituents who were owners
 */
export function getOwners(humanMadeObject, returnIdsOnly = false) {
  let changedOwnership = normalizeFieldToArray(
    humanMadeObject,
    CHANGED_OWNERSHIP_THROUGH
  );
  let stubs = [];
  let indexedIds = [];
  changedOwnership.forEach((activity) => {
    let constituents = normalizeFieldToArray(activity, "transferred_title_to");
    constituents.forEach((constituent) => {
      let indexedId = getIndexedId(constituent.id);
      if (indexedId in indexedIds == false) {
        indexedIds.push(indexedId);
        stubs.push(constituent);
      }
    });
  });

  if (returnIdsOnly) {
    return indexedIds;
  }

  return stubs;
}

/**
 * Gets the "Place Found" with the additional metadata for the Place
 *
 * @param {object} humanMadeObject - the JSON-LD representation of the HumanMadeObject
 *
 * @returns {object} - an object that shares `timespan` and `remarks` with the ownership entries
 */
export function getProvenancePlaceFound(humanMadeObject) {
  let found = getClassifiedAs(
    normalizeFieldToArray(humanMadeObject, REFERRED_TO_BY),
    internal.PLACE_FOUND
  )[0];

  let current_keeper = normalizeFieldToArray(humanMadeObject, "current_keeper");

  // limit to just things that have a place found AND the things from the antiquities department
  if (
    found == undefined ||
    current_keeper.filter(
      (keeper) =>
        keeper.id == internal.ANTIQUITIES_DEPARTMENT ||
        keeper.id == internal.ANTIQUITIES_DEPARTMENT_STAGING
    ).length < 1
  ) {
    return;
  }

  // create a pseudo entry that goes with the owners but with a place instead of an owner.
  let pseudoOwner = {
    place: getValueOrContent(found),
    remarks: getValueOrContent(getAssignedBy(found, REFERRED_TO_BY)[0])
  };

  // check if there is an available timespan to use, if not, use "-"
  const timespan = getAssignedBy(found, "timespan");
  if (timespan.length == 0) {
    pseudoOwner.timespan = "-";
  } else {
    pseudoOwner.timespan = getValueOrContent(timespan[0].identified_by[0]);
  }

  return pseudoOwner;
}

/**
 * Gets all available provenanace data to be displayed on object page
 *
 * @param {object} humanMadeObject  - the JSON-LD HumanMadeObject
 * @param {array} owners - an array of owner objects from the store
 *
 * @returns {array} - an array of objects containing provenanace data
 */
export function getProvenanceData(
  humanMadeObject,
  owners = [],
  unknownOwners = []
) {
  let changedOwnership = normalizeFieldToArray(
    humanMadeObject,
    CHANGED_OWNERSHIP_THROUGH
  );

  if (humanMadeObject == undefined || !changedOwnership.length) {
    return undefined;
  }

  let acquisitions = sortAcquisitions(changedOwnership);

  // take the list of owner data passed in and make it a map/dictionary
  // that's easier to search through for the below mapping
  let ownerData = {};
  owners = owners.filter((owner) => owner != undefined);

  owners.forEach((owner) => {
    ownerData[getIndexedId(owner.id)] = owner;
  });

  acquisitions = acquisitions
    .map((acquisition) => {
      let owners = [];
      let transferred_title_to = normalizeFieldToArray(
        acquisition,
        "transferred_title_to"
      );

      sortByAssignedDimension(transferred_title_to).forEach((owner) => {
        let ownerIndexedId = getIndexedId(owner.id);
        const source = ownerData[ownerIndexedId];

        let data = {
          id: ownerIndexedId,
          prefix: getJoiningPrefix(owner),
          suffix: getSuffix(owner),
          name: getName(source),
          life_dates: localActorHelpers.getLifeDates(source),
          lugtNumber: getLugtNumber(acquisition),
          nationality_and_dates:
            localActorHelpers.getNationalityAndDates(source)
        };

        if (
          isOwnerPrivate(source) ||
          isGroupTheGetty(source) ||
          unknownOwners.includes(source?.id)
        ) {
          data["hideLink"] = true;
        }

        owners.push(data);
      });

      if (!isDeaccessioned(acquisition)) {
        let result = {
          citation: getProvenanceCitation(acquisition),
          location: getProvenanceLocation(acquisition),
          owners: owners,
          remarks: getProvenanceRemarks(acquisition),
          saleNote: getProvenanceSaleNote(acquisition),
          statement: getProvenanceStatement(acquisition),
          timespan: getProvenanceTimespan(acquisition),
          naziEraGaps: hasNaziEraGaps(acquisition),
          naziEraRestitution: hasNaziEraRestitution(acquisition)
        };

        return removeEmpty(result);
      }
    })
    .filter((acq) => acq);

  // inject the place_found as the 1st element
  let found = getProvenancePlaceFound(humanMadeObject);

  if (found != undefined) {
    acquisitions = [found, ...acquisitions];
  }
  return acquisitions;
}

/**
 * Determines whether the acquisition has any Nazi Era Gaps
 *
 * @param {object} acquisition - the JSON-LD acquisition object
 *
 * @returns {boolean}
 */
export function hasNaziEraGaps(acquisition) {
  return hasAssignedClassification(acquisition, internal.NAZI_ERA_GAPS);
}

/**
 * Finds the assigned classification on the acquisition
 *
 * @param {object} acquisition - the JSON-LD acquisition record
 * @param {string} classifiedAs - the classification type for the query
 * @private
 *
 * @returns {boolean}
 */
function hasAssignedClassification(acquisition, classifiedAs) {
  let assigned = getAssignedBy(acquisition, CLASSIFIED_AS);
  if (assigned.length > 0) {
    let results = assigned.filter(
      (classification) => classification.id == classifiedAs
    );
    if (results.length > 0) {
      return true;
    }
  }
  return false;
}

/**
 * Returns whether the owner is classified as "Private" or not
 *
 * @param {object} owner - the JSON-LD object for a Person or Group
 *
 * @returns {boolean}
 */
export function isOwnerPrivate(owner) {
  return getClassifiedAs(owner, aat.PRIVATE).length > 0;
}

/**
 * Returns whether a provenance entry is an empty deaccessioned entry
 *
 * @param {object} owner - the JSON-LD object for a Person or Group
 *
 * @returns {boolean}
 */
export function isDeaccessioned(acquisition) {
  return getClassifiedAs(acquisition, aat.DEACCESSIONED).length > 0;
}

/**
 * Determines whether the acquisition has any Nazi Era Restitution records
 *
 * @param {object} acquisition - the JSON-LD acquisition object
 *
 * @returns {boolean}
 */
export function hasNaziEraRestitution(acquisition) {
  return hasAssignedClassification(acquisition, internal.NAZI_ERA_RESTITUTED);
}

/**
 * Transforms the provenance data for the indexer
 *
 * @param {object} provenanceData - the generic provenance data to be processed for the indexer
 *
 * @returns  {object}
 */
export function cleanupForIndexer(provenanceData) {
  if (provenanceData == undefined) {
    return;
  }
  return provenanceData.map((rec) => {
    let owners = [];
    if (rec.owners) {
      // transform each owner
      rec.owners.forEach((owner) => {
        let indexedOwner = {
          id: owner.id,
          primary_name: owner.name,
          "primary_name--text": owner.name,
          life_dates: owner.life_dates,
          nationality_and_dates: owner.nationality_and_dates,
          lugtNumber: owner.lugtNumber,
          prefix: owner.prefix
        };

        // delete fields of 'only' undefined values
        for (let field of [
          "life_dates",
          "lugtNumber",
          "prefix",
          "nationality_and_dates"
        ]) {
          if (
            indexedOwner[field] == undefined ||
            indexedOwner[field].length == 0
          ) {
            delete indexedOwner[field];
          }
        }
        owners.push(indexedOwner);
      });
      rec.owner = owners;
    }
    // rename our fields
    for (let field of [
      "citation",
      "remarks",
      "saleNote",
      "statement",
      "timespan",
      "location"
    ]) {
      if (rec[field] != undefined) {
        rec[field + "--text"] = rec[field];
      }
      delete rec[field];
    }

    // delete extra fields
    for (let field of ["owners", "naziEraGaps", "naziEraRestitution"]) {
      delete rec[field];
    }
    return rec;
  });
}

/**
 * An internal function to centralize checking whether the HumanMadeObject contains Nazi Era Gaps or Restitution Records
 *
 * @param {object} humanMadeObject - the HumanMadeObject JSON-LD
 * @param {function} callback - the callback function to test whether the entry contains the value
 *
 * @private
 * @returns {boolean}
 */
function hasNaziEraAttribution(humanMadeObject, callback) {
  let changedOwnership = normalizeFieldToArray(
    humanMadeObject,
    CHANGED_OWNERSHIP_THROUGH
  );

  if (humanMadeObject == undefined || !changedOwnership.length) {
    return false;
  }

  // iterate through each acquisition
  for (let i = 0; i < changedOwnership.length; i++) {
    let acquisition = changedOwnership[i];
    let parts = normalizeFieldToArray(acquisition, PART_OF);

    // the by each part
    for (let j = 0; j < parts.length; j++) {
      let part = parts[j];
      if (callback(part)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Returns whether the HumanMadeObject contains Nazi Era gaps
 *
 * @param {object} humanMadeObject - the HumanMadeObject JSON-LD
 *
 * @returns {boolean}
 */
export function objectHasNaziEraGaps(humanMadeObject) {
  return hasNaziEraAttribution(humanMadeObject, hasNaziEraGaps);
}

/**
 * Returns whether the HumanMadeObject contains Nazi Era restitution records
 *
 * @param {object} humanMadeObject - the HumanMadeObject JSON-LD
 *
 * @returns {boolean}
 */
export function objectHasNaziEraRestitution(humanMadeObject) {
  return hasNaziEraAttribution(humanMadeObject, hasNaziEraRestitution);
}

/**
 * Extracts out the prefix (if it exists) for a person (could be 'probably' or the joining 'and')
 *
 * @param {object} owner - the `transferred_title_to` reference to an actor
 *
 * @returns {string} the prefix text
 */
export function getJoiningPrefix(owner) {
  let assigned = getAssignedBy(owner, REFERRED_TO_BY);
  return getValueByClassification(assigned, internal.ACTOR_NAME_PREFIX);
}

/**
 * Extracts out the suffix (if it exists) for a person
 *
 * @param {object} owner - the `transferred_title_to` reference to an actor
 *
 * @returns {string} the suffix text
 */
export function getSuffix(owner) {
  let assigned = getAssignedBy(owner, REFERRED_TO_BY);
  return getValueByClassification(assigned, internal.ACTOR_NAME_SUFFIX);
}

/**
 * Gets the provenance location from an acquisition
 *
 * @param {object} acquisition - the acquisition object
 *
 * @returns {string} - the provenance location
 */
export function getProvenanceLocation(acquisition) {
  let referredToBy = normalizeFieldToArray(
    getFirstAcquisitionPart(acquisition),
    REFERRED_TO_BY
  );

  return getValueByClassification(referredToBy, aat.PROVENANCE_LOCATION);
}

/**
 * Gets the provenance remarks from an acquisition
 *
 * @param {object} acquisition - the acquisition object
 *
 * @returns {string|undefined} - the provenance remarks
 */
export function getProvenanceRemarks(acquisition) {
  let referredToBy = normalizeFieldToArray(
    getFirstAcquisitionPart(acquisition),
    REFERRED_TO_BY
  );

  return getValueByClassification(referredToBy, aat.PROVENANCE_REMARKS);
}

/**
 * Gets the provenance sale note from an acquisition
 *
 * @param {object} acquisition - the acquisition object
 *
 * @returns {string|undefined} - the provenance sale note
 */
export function getProvenanceSaleNote(acquisition) {
  let referredToBy = normalizeFieldToArray(
    getFirstAcquisitionPart(acquisition),
    REFERRED_TO_BY
  );

  return getValueByClassification(referredToBy, aat.PROVENANCE_SALE_NOTE);
}

/**
 * Gets the provenance statement from an acquisition
 *
 * @param {object} acquisition - the acquisition object
 *
 * @returns {string} - the provenance statement
 */
export function getProvenanceStatement(acquisition) {
  let referredToBy = normalizeFieldToArray(
    getFirstAcquisitionPart(acquisition),
    REFERRED_TO_BY
  );

  return getValueByClassification(referredToBy, aat.PROVENANCE_STATEMENT);
}

/**
 * Sorts the list of acquisitions by the order defined by the part's ends_before_the_start_of
 *
 * @param {array} acquisitions - a list of the acquisitions
 *
 * @returns {array} - the sorted list
 */
export function sortAcquisitions(acquisitions) {
  let idMap = {};
  let current = undefined;
  if (acquisitions == undefined || acquisitions.length == 0) {
    return [];
  }
  // loop through all of the acquisitions and find (a) the first and also create a map of ids => acqusiitions
  acquisitions.forEach((acquisition) => {
    let part = getFirstAcquisitionPart(acquisition);
    idMap[part.id] = acquisition;
    if (part.starts_after_the_end_of == undefined) {
      current = acquisition;
    }
  });

  // while we have a current, find the next and add it it to sorted
  let sorted = [current];
  while (
    getFirstAcquisitionPart(current).ends_before_the_start_of != undefined
  ) {
    let nextId =
      getFirstAcquisitionPart(current).ends_before_the_start_of[0].id;
    current = idMap[nextId];
    if (current == undefined) {
      break;
    }
    sorted.push(current);
  }

  return sorted;
}

/**
 * Gets the citation from an acquisition
 *
 * @param {object} acquisition
 * @returns {string}
 */
export function getProvenanceCitation(acquisition) {
  let referredToBy = normalizeFieldToArray(
    getFirstAcquisitionPart(acquisition),
    REFERRED_TO_BY
  );
  let citation = getValueByClassification(
    referredToBy,
    internal.PROVENANCE_CITATION
  );
  return citation;
}

/**
 * helper to get the part
 *
 * @param {object} acquisition
 * @returns {object}
 */
function getFirstAcquisitionPart(acquisition) {
  let firstPart = normalizeFieldToArray(acquisition, PART_OF);
  if (firstPart.length) {
    return firstPart[0];
  }
  return undefined;
}

/**
 * Gets the human-readable version of the provenance timespan
 *
 * @param {Object} acquisition - the JSON-LD Acquisition record
 *
 * @returns {string|undefined} - the textual version of the date
 */
export function getProvenanceTimespan(acquisition) {
  let part = getFirstAcquisitionPart(acquisition);
  let timespan = part.timespan;
  if (timespan == undefined) {
    return undefined;
  }

  const identified_by = normalizeFieldToArray(timespan, IDENTIFIED_BY);
  if (identified_by.length == 0) {
    return undefined;
  }
  return identified_by[0].content;
}
